import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Divider } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { max as maxOf, min as minOf } from "lodash";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery, useGetLayersQuery } from "fond/api";
import { useServiceLocationLayerKey } from "fond/hooks/useCostToServe";
import { CTSType, Project, Store } from "fond/types";
import { BlockSpinner } from "fond/widgets";
import NonIdealState from "fond/widgets/NonIdealState";
import { ZoomableGridCard } from "fond/widgets/ZoomableGridCard";

import { COST_CHART_HEIGHT } from "../constants";

import { getCtsTypeLabel } from "./costChartUtils";
import CostDistributionChart from "./CostDistributionChart";
import CostGaugeSummary from "./CostGaugeSummary";
import CtsTypeSelect from "./CtsTypeSelect";

interface BomCostPerServiceLocationChartProps {
  project: Project;
  versionId: string;
}

const BomCostPerServiceLocationChart: React.FC<BomCostPerServiceLocationChartProps> = ({ project, versionId }) => {
  const slLayerKey = useServiceLocationLayerKey(project);
  const [ctsType, setCtsType] = useState<CTSType>("SharedCost");

  const { isFetching: isFetchingLayers } = useGetLayersQuery(versionId ?? skipToken, { skip: !versionId || !slLayerKey });
  const layer = useSelector((state: Store) =>
    versionId && slLayerKey ? selectLayerByVersionAndLayerKey(state, { versionId, layerId: slLayerKey }) : undefined
  );

  const { data: features, isFetching: isFetchingFeatures } = useGetFeaturesQuery(layer ? { layerId: layer.ID } : skipToken);

  // Read the cost values for the currently selected CTSType from feature properties.
  const costs = useMemo(
    () => (features ?? []).map((feature) => feature.properties?.[ctsType]).filter((value): value is number => typeof value === "number"),
    [features, ctsType]
  );

  const renderContent = () => {
    if (!slLayerKey) {
      return (
        <NonIdealState title="Not available" description="The cost per service location distribution chart is not available for this project type." />
      );
    }

    if (isFetchingLayers || isFetchingFeatures) {
      return <BlockSpinner />;
    }

    if (!layer || !features || features.length <= 0 || costs.length <= 0) {
      return <NonIdealState title="No data available" description="The cost per service location data is not available." />;
    }

    const min = minOf(costs) as number;
    const max = maxOf(costs) as number;
    const average = costs.reduce((sum, cost) => sum + cost, 0) / costs.length;

    return (
      <Box display="flex" flexDirection="column" flex={1} minHeight={0} px={2}>
        <CostGaugeSummary min={min} max={max} average={average} />
        <Divider />
        <CostDistributionChart costs={costs} ctsTypeLabel={getCtsTypeLabel(ctsType)} />
      </Box>
    );
  };

  return (
    <ZoomableGridCard
      breakpoints={{ lg: 6, xs: 12 }}
      title="Cost per service location"
      height={COST_CHART_HEIGHT}
      headerRightElement={slLayerKey ? <CtsTypeSelect value={ctsType} onChange={setCtsType} /> : null}
    >
      {renderContent()}
    </ZoomableGridCard>
  );
};

export default BomCostPerServiceLocationChart;
